import Image from "next/image";
import { useTrans } from "@/utils/useTrans";

/**
 * VR Overview 의 Audio To Haptic 밴드
 * (#5420 · 데스크탑 `30158:114098` 1920×734 · 모바일 `29942:16940` 360×1484).
 *
 * ## ★시안이 개정됐다 — 종전 구현이 인용하던 노드는 **삭제됐다**
 *
 * 이 파일은 원래 데스크탑 `29889:100648`(1920×584) · 모바일 `29942:11912`(360×442)를
 * 근거로 쓰였고, *"모바일 시안에는 일러스트가 없고 텍스트 3단뿐"* 이라고 적혀 있었다.
 * 2026-09-02 재확인에서 그 근거 노드들이 **파일에 없는 것으로 확인됐다**:
 *
 * ```
 *   29942:11875  (모바일 Overview 프레임)  → not found
 *   29942:11918  (ath steps)              → not found
 *   29942:16914  (배너 자식)               → not found
 * ```
 *
 * 개정판에는 **모바일에도 사진 3장이 있다.** 「모바일엔 없다」는 판단은 삭제된 판 기준이라
 * 그대로 두면 영구히 빠진다. 그래서 폭별로 다시 잠근다.
 *
 * ⚠️ **노드 id 를 근거로 적을 때는 그 id 가 살아 있는지가 검증 대상이다.** 보드는 세션
 * 사이에 바뀌고, 죽은 id 를 인용한 주석은 조용히 거짓이 된다.
 *
 * ## 두 판이 갈리는 축 — 폭마다 값이 다르다
 *
 * ```
 *                      데스크탑 30158:114098        모바일 29942:16940
 *   컨테이너            px240 py64 · gap40           pb48 · bg-black
 *   배경 사진           밴드 전체(1920×734)          ★header 에만(360×400)
 *   eyebrow            14/18 SemiBold               14/18 Regular
 *   제목                36/44                        24/32
 *   설명                16/22 #dde1e6 tr.5           14/18 white tr.25
 *   체인                가로 3열 · gap80             ★세로 스택 · gap40
 *   item 안 간격        24                           12
 *   사진                280×240 고정 r40             ★비율 280/240 · 폭 유동 r40
 *   header 높이         내용에 맞춤                    ★비율 360/440
 *   각주                컨테이너 직속                 bottom 래퍼 pt40
 * ```
 *
 * 높이 검산 — 시안 프레임 높이와 맞는지 산술로 대조한다(값을 옮겼다는 증거):
 *
 * ```
 *   데스크탑  py128 + header198 + 40 + chain312 + 40 + 각주16          = 734  ✅
 *   모바일    header400 + lists980(300×3 + 40×2) + bottom56 + pb48    = 1484 ✅
 * ```
 *
 * ## ★수용한 결정 — 중간 폭에서 상한을 두지 않는다 (작성자 지정 2026-09-02)
 *
 * 모바일 이미지 넷을 **비율**로 잡았다(header `360/440` · 항목 `280/240`). 종전엔 고정
 * 높이라 360 에서만 맞고 **그 사이 폭에서 어긋났다.** 비율로 바꾸면 전 구간 맞는데,
 * 대신 폭이 커지는 만큼 **키도 커진다**:
 *
 * ```
 *    폭    섹션 높이   header        비율
 *   360      1524     360×440      0.818 ✅
 *   768      3072     768×939      0.818 ✅
 *  1023      4039    1023×1250     0.818 ✅   ← 시안(1484)의 2.7배
 *  1024       734     976×198      데스크탑 전환
 * ```
 *
 * 상한(`max-w` 로 열 묶기 / `max-h` 로 높이 자르기)을 검토하고 **두지 않기로 했다** —
 * 비율 정확성을 우선한다. 태블릿 세로에서 이 섹션의 스크롤이 길다는 것을 **알고 받는다.**
 *
 * ⚠️ **배너(`vr-overview-banner.tsx`)는 반대로 `max-h-[560px]` 를 둔다.** 같은 페이지에서
 * 같은 질문에 답이 다르므로 「불일치」로 보고 통일하려는 시도가 나올 자리다. 배너는 화면
 * 첫 블록이라 상한이 없으면 태블릿에서 **첫 화면을 통째로 덮는다** — 그래서 상한이 있고,
 * 여기는 스크롤 중간이라 없다. 통일하려면 이 차이를 먼저 뒤집어야 한다.
 *
 * 해소 비용: `max-w-[Npx] mx-auto` 한 줄(항목 컨테이너) + 실측 재확인. 되돌리기 쉽다.
 *
 * ## 자산
 *
 * 사진 3장은 **두 판이 동일**하다(md5 대조 — 560×480 = 280×240 @2x). 배경은 다르다:
 * 데스크탑 3840×1468, 모바일 720×800. 종전 `ath-band-bg.webp`(1134×636)는 **어느 판도
 * 아니어서** 쓰지 않는다. 파일명을 새로 쓴 이유는 `public/` 을 같은 이름으로 덮으면 dev 가
 * 옛 것을 계속 주기 때문이다(`docs/pitfalls.md` — public 이미지 캐시).
 */
export default function VrAthBand() {
  const { t } = useTrans(["games"]);

  return (
    <section className="relative w-full overflow-hidden bg-black laptop:bg-transparent">
      {/* 데스크탑은 밴드 전체가 사진이다. 모바일은 header 안에서 따로 깐다. */}
      <Image
        src="/images/games/vr-overview/ath-bg-desktop.webp"
        alt=""
        aria-hidden
        fill
        sizes="100vw"
        className="hidden object-cover laptop:block"
      />

      <div className="relative flex w-full flex-col items-center pb-12 laptop:px-6 laptop:py-16">
        {/**
         * header — 모바일 `30168:156172`(h400 pt40 px16 gap12) · 데스크탑 `30158:114099`(gap24).
         * ★배경 사진이 **모바일에서는 이 블록에만** 깔린다(아래는 검정).
         */}
        <div className="relative flex aspect-[360/440] w-full flex-col items-center gap-3 px-4 pt-10 laptop:aspect-auto laptop:gap-6 laptop:px-0 laptop:pt-0">
          <Image
            src="/images/games/vr-overview/ath-bg-mobile.webp"
            alt=""
            aria-hidden
            fill
            sizes="100vw"
            className="object-cover laptop:hidden"
          />

          {/* `30168:156176` / `30158:114100` — 14/18 · #FFD600. 굵기가 폭마다 다르다. */}
          <p className="relative text-base tracking-[0.25px] text-bhaptics-yellow laptop:font-600">
            {t("AUDIO TO HAPTIC")}
          </p>

          {/* `30168:156173` 24/32 · `30158:114101` 36/44 — 둘 다 Regular white 가운데. */}
          <h2 className="relative whitespace-pre-line text-center text-3xl text-white laptop:text-6xl">
            {t("Turn Every VR Game Sound into Haptics")}
          </h2>

          {/* `30168:156174` 14/18 white tr.25 · `30158:114102` 16/22 #dde1e6 tr.5 */}
          <p className="relative whitespace-pre-line text-center text-base tracking-[0.25px] text-white laptop:text-lg laptop:tracking-[0.5px] laptop:text-coolgray-20">
            {t("Feel VR games outside the supported list through sound.")}
          </p>
        </div>

        {/**
         * 체인 — 모바일 `30168:156197` 세로 gap40 · 데스크탑 `30158:114103` 가로 gap80.
         * 항목 폭은 두 판 다 **280 고정**이다(데스크탑 3×280 + 2×80 = 1000).
         */}
        <div className="flex w-full flex-col items-center gap-10 px-10 laptop:mt-10 laptop:w-auto laptop:flex-row laptop:gap-20 laptop:px-0">
          {CHAIN.map((step) => (
            <div
              key={step.title}
              className="flex w-full flex-col items-center gap-3 laptop:w-[280px] laptop:gap-6"
            >
              {/**
               * `30168:156180` / `30158:114105` — r40.
               *
               * ★**모바일은 높이가 아니라 비율이다**(`280/240`). 고정 높이면 폭이 변할 때
               * 사진만 안 따라와 구도가 잘린다 — 배너에서 같은 판단을 이미 했다.
               * 데스크탑은 시안대로 280×240 고정.
               */}
              <div className="relative aspect-[280/240] w-full overflow-hidden rounded-[40px] laptop:aspect-auto laptop:h-[240px] laptop:w-[280px]">
                <Image
                  src={step.image}
                  alt=""
                  aria-hidden
                  fill
                  sizes="(min-width: 1024px) 280px, calc(100vw - 80px)"
                  className="object-cover"
                />
              </div>

              {/* `30168:156181` — gap8 · 가운데 · text-shadow 0 2 16 rgba(0,0,0,.72). */}
              <div className="flex w-full flex-col gap-2 text-center [text-shadow:0px_2px_16px_rgba(0,0,0,0.72)]">
                {/* 16/22 Medium white tr.15 */}
                <p className="text-lg font-500 tracking-[0.15px] text-white">
                  {t(step.title)}
                </p>
                {/* 14/18 Regular rgba(255,255,255,.72) tr.25 */}
                <p className="text-base tracking-[0.25px] text-white/[0.72]">
                  {t(step.desc)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/**
         * 각주 — 모바일 `30168:156198` 은 `pt40` 래퍼를 따로 두고, 데스크탑
         * `30158:114119` 는 컨테이너 gap40 으로 벌어진다. 값은 같아서 `pt-10` 하나로 쓴다.
         *
         * ⚠️ **「~에서만」은 배타 주장이고 리포로 검증되지 않는다** — 시안 문구를 그대로
         * 옮기되 제품 확인이 필요한 사실로 남겨 둔다(사용자에게 단정하는 문장이다).
         */}
        <p className="relative pt-10 text-center text-sm tracking-[0.4px] text-coolgray-50">
          {t(
            "Audio haptics is supported only on PC Player, Mobile Player(Android) and VR Player.",
          )}
        </p>
      </div>
    </section>
  );
}

/**
 * `30168:156179`·`156184`·`156189` — 3단계. 사진은 두 판이 동일하다.
 *
 * `title`·`desc` 는 `t()` 를 지나는 **동적 키**라 리터럴 스캔이 못 본다. 형제
 * `ZONES` 와 같은 이유로 export 해서 `vr-games-keys-resolve` 가 7로케일 해석을
 * 파생 확인한다(#5533 리뷰 n3) — 단계를 추가하면 그 가드가 걸린다.
 */
export const CHAIN = [
  {
    title: "Detects Sound",
    desc: "Recognizes sound from all games, movies, and music.",
    image: "/images/games/vr-overview/ath-item-sound.webp",
  },
  {
    title: "Real-Time Analysis",
    desc: "Analyzes audio in real time and turns it into haptic feedback.",
    image: "/images/games/vr-overview/ath-item-analysis.webp",
  },
  {
    title: "Delivers to Your Body",
    desc: "TactSuit brings the rhythm and intensity of sound to your body.",
    image: "/images/games/vr-overview/ath-item-vibration.webp",
  },
] as const;
